import { useState } from 'react';
import { CheckOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import type { UngDungCoBiMat } from '../types';

/** Thời gian nút hiện "Đã chép" trước khi về lại "Sao chép". */
const GIU_DA_CHEP_MS = 2000;

/**
 * Bí mật ký webhook vừa cấp — **một lần duy nhất**, cùng khuôn `HopBiMatKhoaApi` của màn Khoá API.
 *
 * Máy chủ không lưu bí mật (dẫn xuất từ khoá gốc, ADR-0085 mục 2) và không có đường nào trả lại nó; đóng hộp là mất.
 * Nên bấm ra ngoài không đóng hộp — chỉ nút "Tôi đã lưu bí mật", nút đóng và Esc.
 *
 * Bí mật nằm trong props của hộp này, không trong cache của react-query: cache sống qua lượt điều hướng.
 *
 * `webhookSecret: null` là máy chủ **chưa có khoá gốc**: ứng dụng vẫn tạo được, bí mật cấp sau. Hộp nói thẳng điều đó
 * thay vì hiện một ô trống.
 */
export function HopBiMatWebhook({ vuaCap, onClose }: { vuaCap: UngDungCoBiMat | null; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const [daChep, setDaChep] = useState(false);
  const biMat = vuaCap?.webhookSecret ?? null;

  const chep = () => {
    if (!biMat) return;
    // Clipboard API chỉ có trên https hoặc localhost; thiếu thì nói để người dùng tự chọn chữ.
    const ghi = navigator.clipboard?.writeText(biMat) ?? Promise.reject(new Error('clipboard'));
    ghi.then(
      () => {
        setDaChep(true);
        toast.success(t('ekyc.ungDung.daChepBiMat'));
        window.setTimeout(() => setDaChep(false), GIU_DA_CHEP_MS);
      },
      () => toast.warning(t('ekyc.ungDung.khongChepDuoc')),
    );
  };

  return (
    <Dialog
      open={vuaCap !== null}
      onClose={onClose}
      title={t('ekyc.ungDung.biMatTieuDe', { ten: vuaCap?.ten ?? '' })}
      width={600}
      maskClosable={false}
      destroyOnHidden
      footer={
        <div className="flex justify-end">
          <Button variant="primary" size="md" onClick={onClose}>
            {biMat ? t('ekyc.ungDung.daLuuBiMat') : t('chung.dong')}
          </Button>
        </div>
      }
    >
      {biMat ? (
        <div className="space-y-4 pt-2">
          <p role="note" className="flex items-start gap-2 rounded bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700">
            <WarningOutlined className="mt-0.5 shrink-0" />
            <span className="min-w-0">{t('ekyc.ungDung.biMatCanhBao')}</span>
          </p>
          <Field label={t('ekyc.ungDung.biMatNhan')} hint={t('ekyc.ungDung.biMatGoiY', { so: vuaCap?.phienBanBiMat ?? 0 })}>
            {(p) => (
              <div className="flex min-w-0 gap-2">
                <Input
                  {...p}
                  readOnly
                  className="min-w-0 flex-1 font-mono text-xs"
                  value={biMat}
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="secondary" size="md" icon={daChep ? <CheckOutlined /> : <CopyOutlined />} onClick={chep}>
                  {daChep ? t('ekyc.ungDung.daChep') : t('ekyc.ungDung.saoChep')}
                </Button>
              </div>
            )}
          </Field>
        </div>
      ) : (
        <p className="pt-2 text-sm text-content-secondary">{t('ekyc.ungDung.chuaCoKhoaGocKhiTao')}</p>
      )}
    </Dialog>
  );
}
